import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrderDetailsModal = ({ order, isOpen, onClose, onStatusUpdate, onSaveNotes }) => {
  const [workNotes, setWorkNotes] = useState(order?.workNotes || '');
  const [mileage, setMileage] = useState(order?.vehicle?.mileage || '');
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !order) return null;

  const handlePhotoUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const newPhotos = files?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      url: URL.createObjectURL(file),
      name: file?.name
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev?.filter(photo => photo?.id !== photoId));
  };

  const handleCompleteWork = async () => {
    setIsSubmitting(true);
    try {
      await onSaveNotes(order?.id, {
        workNotes,
        mileage: parseInt(mileage),
        photos: photos?.map(p => p?.file)
      });
      await onStatusUpdate(order?.id, 'DONE');
      onClose();
    } catch (error) {
      console.error('Error completing work:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-1300 flex items-end sm:items-center justify-center p-4">
      <div className="bg-card rounded-t-lg sm:rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">Auftragsdetails</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-4 space-y-4">
            {/* Order Info */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-semibold mb-2">Auftrag #{order?.orderNumber}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-text-secondary">Status:</span>
                  <span className="ml-2 font-medium">
                    {order?.status === 'ASSIGNED' && 'Zugewiesen'}
                    {order?.status === 'IN_PROGRESS' && 'In Bearbeitung'}
                    {order?.status === 'DONE' && 'Abgeschlossen'}
                  </span>
                </div>
                <div>
                  <span className="text-text-secondary">Priorität:</span>
                  <span className="ml-2 font-medium">
                    {order?.priority === 'HIGH' && 'Hoch'}
                    {order?.priority === 'NORMAL' && 'Normal'}
                    {order?.priority === 'LOW' && 'Niedrig'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-text-secondary">Erstellt:</span>
                  <span className="ml-2">{formatDate(order?.createdAt)}</span>
                </div>
                {order?.scheduledDate && (
                  <div className="col-span-2">
                    <span className="text-text-secondary">Termin:</span>
                    <span className="ml-2">{formatDate(order?.scheduledDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-blue-800">Fahrzeugdaten</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-600">Marke/Modell:</span>
                  <span className="font-medium">{order?.vehicle?.make} {order?.vehicle?.model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">VIN:</span>
                  <span className="font-mono text-xs">{order?.vin}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Baujahr:</span>
                  <span>{order?.vehicle?.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Kennzeichen:</span>
                  <span>{order?.vehicle?.licensePlate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Farbe:</span>
                  <span>{order?.vehicle?.color}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Motor:</span>
                  <span>{order?.vehicle?.engine}</span>
                </div>
              </div>
            </div>

            {/* Service Information */}
            <div className="bg-amber-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-amber-800">Service-Informationen</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-amber-600 font-medium">Service-Typ:</span>
                  <p className="mt-1">{order?.serviceType}</p>
                </div>
                {order?.description && (
                  <div>
                    <span className="text-amber-600 font-medium">Beschreibung:</span>
                    <p className="mt-1">{order?.description}</p>
                  </div>
                )}
                {order?.serviceItems && order?.serviceItems?.length > 0 && (
                  <div>
                    <span className="text-amber-600 font-medium">Service-Positionen:</span>
                    <ul className="mt-1 space-y-1">
                      {order?.serviceItems?.map((item, index) => (
                        <li key={index} className="flex justify-between">
                          <span>{item?.description}</span>
                          <span className="font-medium">{item?.price?.toFixed(2)} €</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Customer & Dealership */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-green-800">Kontaktdaten</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-green-600 font-medium">Autohaus:</span>
                  <p className="mt-1">{order?.dealership?.name}</p>
                  <p className="text-green-600">
                    {order?.dealership?.address?.street}<br />
                    {order?.dealership?.address?.zipCode} {order?.dealership?.address?.city}
                  </p>
                  <p className="text-green-600">Tel: {order?.dealership?.phone}</p>
                </div>
                {order?.customerContact && (
                  <div>
                    <span className="text-green-600 font-medium">Kundenkontakt:</span>
                    <p className="mt-1">{order?.customerContact?.name}</p>
                    <p className="text-green-600">Tel: {order?.customerContact?.phone}</p>
                    {order?.customerContact?.email && (
                      <p className="text-green-600">E-Mail: {order?.customerContact?.email}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Work Progress */}
            {order?.status === 'IN_PROGRESS' && (
              <div className="space-y-4">
                <h3 className="font-semibold">Arbeitsfortschritt</h3>
                
                <Input
                  label="Kilometerstand"
                  type="number"
                  placeholder="Aktueller Kilometerstand"
                  value={mileage}
                  onChange={(e) => setMileage(e?.target?.value)}
                />

                <div>
                  <label className="block text-sm font-medium mb-2">Arbeitsnotizen</label>
                  <textarea
                    className="w-full p-3 border border-border rounded-lg resize-none"
                    rows="4"
                    placeholder="Beschreiben Sie die durchgeführten Arbeiten..."
                    value={workNotes}
                    onChange={(e) => setWorkNotes(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Fotos hinzufügen</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="flex items-center justify-center w-full p-4 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                  >
                    <Icon name="Camera" size={20} className="mr-2" />
                    Fotos aufnehmen/auswählen
                  </label>
                  
                  {photos?.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {photos?.map((photo) => (
                        <div key={photo?.id} className="relative">
                          <img
                            src={photo?.url}
                            alt={photo?.name}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => removePhoto(photo?.id)}
                            className="absolute top-1 right-1 w-6 h-6"
                          >
                            <Icon name="X" size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-border bg-muted">
          <div className="flex space-x-2">
            {order?.status === 'ASSIGNED' && (
              <Button
                variant="default"
                onClick={() => onStatusUpdate(order?.id, 'IN_PROGRESS')}
                className="flex-1"
                iconName="Play"
                iconPosition="left"
              >
                Arbeit starten
              </Button>
            )}
            {order?.status === 'IN_PROGRESS' && (
              <Button
                variant="success"
                onClick={handleCompleteWork}
                loading={isSubmitting}
                className="flex-1"
                iconName="Check"
                iconPosition="left"
              >
                Arbeit abschließen
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;